import classNames from "classnames";
import find from "lodash/find";
import includes from "lodash/includes";
import { Alert } from "PFComponents/alert";
import { Flex } from "PFComponents/containers";
import { parseValidationErrors } from "PFCore/helpers/api_errors/api_error_responses";
import { getErrorFieldName } from "PFCore/helpers/api_errors/response_errors_utils";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { ApiValidationError } from "PFTypes";
import { useState } from "react";
import { TFunction, useTranslation } from "react-i18next";

import css from "./ai_generator.module.scss";
import { Header } from "./parts";
import { CollapsedBar } from "./parts/collapsed_bar";
import { Footer } from "./parts/footer";

type OnGenerateParams<T> = {
  longer?: boolean;
  regenerate?: boolean;
  selectedGeneratedData?: T;
};

type GenerationErrorAlert = {
  kind: "warning" | "error";
  title: string;
};

const GEN_AI_INSUFFICIENT_DATA = "errors.gen_ai.insufficient_data";
const GEN_AI_MISSING_FIELD = "errors.gen_ai.missing_field";

const getTranslationKey = (error: ApiValidationError) => getAttribute(error.meta, "translationKey");

const getGenerationErrorAlert = (
  t: TFunction,
  error: unknown,
  errorMessage: string,
  fieldLabels?: Record<string, string>
): GenerationErrorAlert => {
  const genAiError = find(parseValidationErrors(error), (validationError) =>
    includes([GEN_AI_INSUFFICIENT_DATA, GEN_AI_MISSING_FIELD], getTranslationKey(validationError))
  );

  if (!genAiError) {
    return { kind: "error", title: errorMessage };
  }

  if (getTranslationKey(genAiError) === GEN_AI_INSUFFICIENT_DATA) {
    return { kind: "warning", title: t("errors.genAi.insufficientData") };
  }

  const pointer = genAiError.source?.pointer;
  const fieldLabel = pointer ? fieldLabels?.[getErrorFieldName(pointer)] : undefined;

  return {
    kind: "error",
    title: fieldLabel
      ? t("errors.genAi.missingField", { field: fieldLabel })
      : t("errors.genAi.missingFieldFallback")
  };
};

export type OnGenerateFunction<T extends object> = (params: OnGenerateParams<T>) => Promise<T>;

export type AIGeneratorProps<T extends object> = {
  message: string;
  buttonText: string;
  errorMessage: string;
  PreviewComponent: React.FC<{ data: T }>;
  enabled: boolean;
  allowMakeLonger?: boolean;
  className?: string;
  fieldLabels?: Record<string, string>;
  onGenerate: OnGenerateFunction<T>;
  onInsert: (payload: T) => void;
};

export const AIGenerator = <T extends object>({
  message,
  buttonText,
  errorMessage,
  PreviewComponent,
  className,
  enabled,
  allowMakeLonger = true,
  fieldLabels,
  onGenerate,
  onInsert
}: AIGeneratorProps<T>) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedData, setGeneratedData] = useState<T[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [generationError, setGenerationError] = useState<GenerationErrorAlert | null>(null);

  const selectedGeneratedData = generatedData[selectedIndex];

  const handleGenerate = (params: OnGenerateParams<T> = {}) => {
    setGenerationError(null);
    setIsLoading(true);
    onGenerate(params)
      .then((response) => {
        const newGeneratedData = [...generatedData, response];
        setGeneratedData(newGeneratedData);
        setSelectedIndex(newGeneratedData.length - 1);
        setIsLoading(false);
      })
      .catch((error) => {
        setGenerationError(getGenerationErrorAlert(t, error, errorMessage, fieldLabels));
        setIsLoading(false);
      });
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (generatedData.length === 0) {
      handleGenerate();
    }
  };

  if (!isOpen) {
    return (
      <CollapsedBar
        enabled={enabled}
        message={message}
        buttonText={buttonText}
        onOpen={handleOpen}
        className={className}
      />
    );
  }

  return (
    <Flex
      direction="column"
      gap="spacingLg"
      className={classNames(css.root, { [css.open]: isOpen, [css.loading]: isLoading }, className)}
    >
      <Header
        onClose={() => setIsOpen(false)}
        isLoading={isLoading}
        count={generatedData.length}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
      />
      {generationError && <Alert kind={generationError.kind} title={generationError.title} multiLineTitle />}
      {selectedGeneratedData && <PreviewComponent data={selectedGeneratedData} />}
      {selectedGeneratedData && (
        <Footer
          allowMakeLonger={allowMakeLonger}
          onTryAgain={() => handleGenerate({ regenerate: true, selectedGeneratedData })}
          onMakeLonger={() => handleGenerate({ longer: true, selectedGeneratedData })}
          onInsert={() => onInsert(selectedGeneratedData)}
        />
      )}
    </Flex>
  );
};
